import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Radio,
  RadioGroup,
  Typography,
} from "@mui/material";
import LoadingButton from "@mui/lab/LoadingButton";

import { showNotification } from "../../../../../constants";
import { uploadPublicFile } from "../../hooks/SalesPagesHook";
import {
  DEFAULT_IMAGE_SIZE,
  IMAGE_SIZE_OPTIONS,
  IMAGE_SIZE_PRESETS,
  describeOptimization,
  formatBytes,
  isOptimizableImage,
  optimizeImageFile,
} from "../../../../../utils/imageOptimization";

const formatSize = (bytes) => (bytes ? formatBytes(bytes) : "");

const UploadFileModal = ({ open, onClose, onUploaded }) => {
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const [size, setSize] = useState(DEFAULT_IMAGE_SIZE);

  useEffect(() => {
    if (!open) {
      setFile(null);
      setUploading(false);
      setDragActive(false);
      setSize(DEFAULT_IMAGE_SIZE);
    }
  }, [open]);

  const canOptimize = isOptimizableImage(file);

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (uploading) return;
    setDragActive(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    // Ignore leave events fired when moving over child elements.
    if (e.currentTarget.contains(e.relatedTarget)) return;
    setDragActive(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (uploading) return;
    const files = e.dataTransfer?.files;
    if (!files || files.length === 0) return;
    if (files.length > 1) {
      showNotification("Only one file can be uploaded at a time");
    }
    setFile(files[0]);
  };

  const handleUpload = async () => {
    if (!file) {
      showNotification("Please choose a file");
      return;
    }
    try {
      setUploading(true);
      if (canOptimize) {
        const result = await optimizeImageFile(file, {
          maxDimension: IMAGE_SIZE_PRESETS[size].maxDimension,
        });
        await uploadPublicFile(result.file, {
          originalName: file.name,
          imageSize: size,
        });
        showNotification(describeOptimization(result));
      } else {
        await uploadPublicFile(file);
        showNotification("File uploaded");
      }
      onUploaded();
    } catch (error) {
      console.log("Error uploading file:", error.message);
      showNotification("Error uploading file");
    } finally {
      setUploading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle sx={titleStyles}>Upload File</DialogTitle>
      <DialogContent>
        <Box
          onDragEnter={handleDragOver}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          sx={{
            ...dropZoneStyles,
            borderColor: dragActive ? "#00A7CD" : "#B0BEC5",
            background: dragActive ? "#E6F7FB" : "#FAFAFA",
          }}
        >
          <Typography sx={dropTextStyles}>
            {dragActive ? "Drop file here" : "Drag and drop a file here, or"}
          </Typography>
          <Button
            variant="outlined"
            component="label"
            style={pickStyles}
            disabled={uploading}
          >
            Choose File
            <input
              hidden
              type="file"
              onChange={(e) => {
                setFile(e.target.files?.[0] || null);
                e.target.value = "";
              }}
            />
          </Button>
        </Box>
        {file ? (
          <Typography sx={fileStyles}>
            {file.name}
            {file.size ? ` (${formatSize(file.size)})` : ""}
          </Typography>
        ) : null}
        {file && canOptimize ? (
          <Box sx={{ mt: 2 }}>
            <Typography sx={sizeLabelStyles}>Max image size</Typography>
            <RadioGroup
              row
              value={size}
              onChange={(e) => setSize(e.target.value)}
            >
              {IMAGE_SIZE_OPTIONS.map((opt) => (
                <FormControlLabel
                  key={opt.value}
                  value={opt.value}
                  control={<Radio size="small" disabled={uploading} />}
                  label={opt.label}
                />
              ))}
            </RadioGroup>
            <Typography sx={{ ...helperStyles, mt: 0.5 }}>
              Images are resized to fit the longest side and converted to WebP.
              GIF/SVG files are uploaded unchanged.
            </Typography>
          </Box>
        ) : null}
        {file ? null : (
          <Typography sx={helperStyles}>
            The file is stored in the uploads folder and referenced by its
            public URL.
          </Typography>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} style={cancelStyles}>
          Cancel
        </Button>
        <LoadingButton
          variant="contained"
          loading={uploading}
          disabled={!file}
          onClick={handleUpload}
          style={uploadStyles}
        >
          Upload
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
};

export default UploadFileModal;

const titleStyles = {
  fontFamily: "Avenir Book, sans-serif",
  fontWeight: 700,
};

const dropZoneStyles = {
  border: "2px dashed",
  borderRadius: 2,
  p: 4,
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  gap: 1.5,
  transition: "background 0.15s, border-color 0.15s",
};

const dropTextStyles = {
  fontFamily: "Avenir Book, sans-serif",
  fontSize: 14,
  color: "#808080",
};

const pickStyles = {
  textTransform: "none",
  color: "#00A7CD",
  borderColor: "#00A7CD",
  fontFamily: "Avenir Book, sans-serif",
};

const fileStyles = {
  fontFamily: "Avenir Book, sans-serif",
  fontSize: 14,
  color: "#020303",
  mt: 2,
};

const helperStyles = {
  fontFamily: "Avenir Book, sans-serif",
  fontSize: 12,
  color: "#808080",
  mt: 2,
};

const sizeLabelStyles = {
  fontFamily: "Avenir Book, sans-serif",
  fontWeight: 600,
  fontSize: 14,
  color: "#0A0A0AAB",
};

const cancelStyles = {
  textTransform: "none",
  color: "#00A7CD",
  fontFamily: "Avenir Book, sans-serif",
};

const uploadStyles = {
  textTransform: "none",
  background: "#00A7CD",
  fontFamily: "Avenir Book, sans-serif",
};
